'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';

export default function ContactFormSection() {
  const router = useRouter();
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: 'suiting',
    quantity: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('generate_lead', {
      form_name: 'contact_page_inquiry',
      fabric_interest: form.interest,
    });
    router.push('/contact/confirmation');
  };

  return (
    <section className="bg-background py-20 px-6 md:px-12 border-b border-border">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left — info */}
        <div className="lg:col-span-4 flex flex-col justify-between gap-10">
          <div>
            <div className="section-label mb-6">B2B Inquiry Form</div>
            <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] text-foreground leading-tight tracking-tight mb-5">
              Submit Your
              <br />
              <span className="italic text-muted-foreground font-light">Fabric Requirement.</span>
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Fill in your details and fabric requirements. Our team will reach out with pricing,
              availability, and swatches within 24 hours.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-5 border border-border bg-muted">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary mt-0.5 shrink-0"
              >
                <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0z" />
                <circle cx="12" cy="11" r="3" />
              </svg>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
                  Registered Office
                </p>
                <p className="text-foreground text-sm font-medium">H. No. 1533/0, Flat No. 202</p>
                <p className="text-muted-foreground text-xs">Navakar Residency, Anjurphata</p>
                <p className="text-muted-foreground text-xs">Bhiwandi, Thane — 421302, MH</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 border border-border bg-muted">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary mt-0.5 shrink-0"
              >
                <path d="M3 5a2 2 0 0 1 2-2h3.28a1 1 0 0 1 .948.684l1.498 4.493a1 1 0 0 1-.502 1.21l-2.257 1.13a11.042 11.042 0 0 0 5.516 5.516l1.13-2.257a1 1 0 0 1 1.21-.502l4.493 1.498a1 1 0 0 1 .684.949V19a2 2 0 0 1-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
                  Direct Lines
                </p>
                <a
                  href="tel:+919321145843"
                  className="text-foreground text-sm font-medium hover:text-primary transition-colors block"
                >
                  +91 93211 45843
                </a>
                <a
                  href="tel:+918080138137"
                  className="text-foreground text-sm hover:text-primary transition-colors block"
                >
                  +91 80801 38137
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-8">
          <div className="border border-border bg-card p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Progress indicator */}
              <div className="flex items-center gap-3 mb-8">
                <span className="text-[10px] text-muted-foreground font-mono">01</span>
                <div className="h-px flex-1 bg-border">
                  <div className="h-full w-1/2 bg-primary" />
                </div>
                <span className="text-[10px] text-muted-foreground font-mono">02</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="form-label">Your Name *</label>
                  <input
                    name="name"
                    type="text"
                    placeholder="Full name"
                    className="form-input"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Company / Business Name *</label>
                  <input
                    name="company"
                    type="text"
                    placeholder="Your company"
                    className="form-input"
                    value={form.company}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="form-label">Email Address</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="email@company.com"
                    className="form-input"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="form-label">Phone Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    className="form-input"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="form-label">Fabric Category *</label>
                  <select
                    name="interest"
                    className="form-input"
                    value={form.interest}
                    onChange={handleChange}
                  >
                    <option value="suiting">Suiting Fabrics</option>
                    <option value="shirting">Shirting Fabrics</option>
                    <option value="both">Both Suiting & Shirting</option>
                    <option value="custom">Custom Composition</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Estimated Quantity</label>
                  <input
                    name="quantity"
                    type="text"
                    placeholder="e.g. 500 metres"
                    className="form-input"
                    value={form.quantity}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Detailed Requirements</label>
                <textarea
                  name="message"
                  rows={4}
                  placeholder="Fabric type, composition, width, colour, delivery timeline, city..."
                  className="form-input resize-none"
                  value={form.message}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4 border-t border-border">
                <p className="text-[11px] text-muted-foreground max-w-xs">
                  By submitting, you agree to be contacted by our team via phone or email regarding
                  your fabric inquiry.
                </p>
                <button type="submit" className="btn-primary whitespace-nowrap">
                  Submit Inquiry
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
